/**
 * StudentActivityScreen — la route `activity`, hors live : l'activité que le lanceur a posée
 * (`useLaunchStudentActivity` → `currentActivity`), sur le design system. À défaut (une page rechargée,
 * un lien), l'activité la plus récente de la todo, comme l'ancien `ActivityWrapperScreen`. L'activité
 * courante est vidée en quittant l'écran, comme l'ancien.
 *
 * Comme l'ancien `ActivityWrapperScreen`, l'état posé avec l'activité choisit l'écran : « finie »
 * (les activités faites, les réalisations, la messagerie), elle se relit (`StudentFinishedActivity`) ;
 * sinon elle s'exécute. « Refaire l'activité » la repose « en cours » : l'exécution prend la place.
 */
import { useNavigation } from "@react-navigation/native"
import React, { useEffect, useMemo } from "react"
import { useDispatch, useSelector, useStore } from "react-redux"

import { setCurrentActivity } from "../../../../../../data/redux/actions/activity"
import { getMostRecentActivity } from "../../../../../../data/utility/dataUtils"
import { type ApiRunActivity } from "../../../../adapters/student/Activity/studentActivityAdapter"
import { StudentActivityBody } from "./StudentActivityBody"
import { StudentFinishedActivity } from "./StudentFinishedActivity"
import { useStudentActivityExit } from "./useStudentActivityExit"
import { useStudentActivityView } from "./useStudentActivityView"
import { selectDisplayedStudent } from "../../../../view-models/shared/displayedStudent"

type CurrentActivity = { activityData?: ApiRunActivity | null; progressStatus?: string } | null
type CurrentActivityRoot = {
    data?: {
        currentActivity?: { activity?: CurrentActivity }
        currentUser?: { object?: { todo_activities?: ApiRunActivity[] } | null }
    }
}

function StudentActivityRun({ activity }: { activity: ApiRunActivity | null }) {
    const navigation = useNavigation() as unknown as { push: (route: string, params?: object) => void }
    const view = useStudentActivityView({
        activity,
        onAddMaterial: (activityId) => navigation.push("new-material", { activityId }),
    })
    const onFinished = useStudentActivityExit(view.nextActivity)
    return <StudentActivityBody view={view} onFinished={onFinished} />
}

export function StudentActivityScreen() {
    const dispatch = useDispatch()
    const current = useSelector((state: CurrentActivityRoot) => state.data?.currentActivity?.activity ?? null)
    const todo = useSelector((state: CurrentActivityRoot) => selectDisplayedStudent(state)?.todo_activities)
    const activity = useMemo(
        () =>
            current?.activityData ??
            (getMostRecentActivity(
                (todo ?? []).filter((a) => !a?.isDeleted),
                null
            ) as ApiRunActivity | null),
        [current?.activityData, todo]
    )

    // Seulement si c'est encore la nôtre : l'activité suivante, posée avant que cet écran parte
    // (`replace`), ne doit pas être vidée (le même filet que `SamSessionScreen`).
    const store = useStore<CurrentActivityRoot>()
    const activityId = activity?.id ?? null
    useEffect(
        () => () => {
            const stillOurs = store.getState().data?.currentActivity?.activity?.activityData?.id === activityId
            if (stillOurs) dispatch(setCurrentActivity(null) as never)
        },
        [dispatch, store, activityId]
    )

    if (current?.progressStatus === "finished" && current.activityData != null) {
        return <StudentFinishedActivity activity={current.activityData} />
    }
    return <StudentActivityRun activity={activity ?? null} />
}
